// Where you were on each page.
//
// A single-page app has no memory of its own: leaving the archive halfway
// down and coming back put you at the top, so scrolling through 118 characters
// meant starting again every time you looked at something else. The browser
// does this for ordinary sites; here it has to be done by hand.
//
// A page you have seen before opens where you left it. A page you have not
// opens at the top. The positions are kept per path for the length of the tab
// (sessionStorage), so a refresh keeps them and a new tab starts clean.

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const KEY = 'scrollMemory';

const read = () => {
  try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (e) { return {}; }
};

const write = (map) => {
  try { sessionStorage.setItem(KEY, JSON.stringify(map)); } catch (e) { /* private mode */ }
};

const ScrollMemory = () => {
  const { pathname } = useLocation();
  const current = useRef(pathname);

  useEffect(() => {
    // Our restore, not the browser's guess.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  // Remember where this page is as it is scrolled, cheaply.
  useEffect(() => {
    current.current = pathname;
    let timer = null;
    const onScroll = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        const map = read();
        map[current.current] = Math.round(window.scrollY);
        write(map);
      }, 150);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timer) clearTimeout(timer);
      // Leaving: keep the last position even if the final scroll never fired.
      const map = read();
      map[pathname] = Math.round(window.scrollY);
      write(map);
    };
  }, [pathname]);

  // Put the page back where it was, once it is tall enough to allow it.
  useEffect(() => {
    const target = read()[pathname] || 0;
    if (!target) {
      window.scrollTo(0, 0);
      return undefined;
    }
    let tries = 0;
    let frame = null;
    const restore = () => {
      const reachable = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo(0, Math.min(target, Math.max(0, reachable)));
      // Lists and images arrive late, so the page grows under us for a moment;
      // keep trying until it can hold the position, then stop.
      if (Math.abs(window.scrollY - target) > 4 && tries < 30) {
        tries += 1;
        frame = setTimeout(restore, 100);
      }
    };
    restore();
    return () => { if (frame) clearTimeout(frame); };
  }, [pathname]);

  return null;
};

export default ScrollMemory;
